import { createAction, createReducer } from '@reduxjs/toolkit'
import React, { createContext, useContext, useMemo, useReducer } from 'react'

export const InvoiceDetailsContext = createContext([])

const actions = {
  setInvoiceStatusModalVisibility: createAction<boolean | undefined>(
    'SET_INVOICE_STATUS_MODAL_VISIBILITY',
  ),
  setInvoicePaymentStatus: createAction<string | any>(
    'SET_INVOICE_PAYMENT_STATUS',
  ),
  setInvoicePaymentMessage: createAction<boolean>(
    'SET_INVOICE_PAYMENT_MESSAGE',
  ),
  setInvoicePaymentMethod: createAction<any>('SET_INVOICE_PAYMENT_METHOD'),
  setPaymentPlanModalVisibility: createAction<boolean>(
    'SET_PAYMENT_PLAN_MODAL_VISIBILITY',
  ),
  setAddToProjectModalVisibility: createAction<boolean>(
    'SET_ADD_TO_PROJECT_MODAL_VISIBILITY',
  ),
  setProjectSelectionModalVisibility: createAction<boolean>(
    'SET_PROJECT_LIST_MODAL_VISIBILITY',
  ),
  setPaymentProcessingModal: createAction<boolean>(
    'SET_PAYMENT_PROCESSING_MODAL',
  ),
  setInviteSuccessModal: createAction<boolean>('SET_PAYMENT_SUCCESS_MODAL'),
  setFoundProject: createAction<any>('SET_FOUND_PROJECT'),
  setAddProjectSuccessModalVisibility: createAction<boolean>(
    'SET_ADD_PROJECT_SUCCESS_MODAL_VISIBILITY',
  ),
  setSelectedProject: createAction<any>('SET_SELECTED_PROJECT'),
  setPayInvoiceModal: createAction<boolean>('SET_PAY_INVOICE_MODAL'),
  setBankSuccess: createAction<boolean>('SET_BANK_SUCCESS'),
  setCardSuccess: createAction<boolean>('SET_CARD_SUCCESS'),
}

function setInvoiceStatusModalVisibilityFunc(state, action) {
  const invoiceStatusModalVisibility = action.payload || false
  return {
    ...state,
    invoiceStatusModalVisibility,
  }
}

function setInvoicePaymentStatusFunc(state, action) {
  const invoicePaymentStatus = action.payload || false
  return {
    ...state,
    invoicePaymentStatus,
  }
}

function setInvoicePaymentMessageFunc(state, action) {
  const invoicePaymentMessage = action.payload || ''
  return {
    ...state,
    invoicePaymentMessage,
  }
}

function setInvoicePaymentMethodFunc(state, action) {
  const invoicePaymentMethod = action.payload || {}
  return {
    ...state,
    invoicePaymentMethod,
  }
}

function setPaymentPlanModalVisibilityFunc(state, action) {
  const paymentPlanModalVisibility = action.payload || false
  return {
    ...state,
    paymentPlanModalVisibility,
  }
}

function setAddToProjectModalVisibilityFunc(state, action) {
  const addToProjectModalVisibility = action.payload || false
  return {
    ...state,
    addToProjectModalVisibility,
  }
}

function setProjectSelectionModalVisibilityFunc(state, action) {
  const projectSelectionModalVisibility = action.payload || false
  return {
    ...state,
    projectSelectionModalVisibility,
  }
}

function setPaymentProcessingModalFunc(state, action) {
  const paymentProcessingModal = action.payload || false
  return {
    ...state,
    paymentProcessingModal,
  }
}

function setInviteSuccessModalFunc(state, action) {
  const inviteSuccessModal = action.payload || false
  return {
    ...state,
    inviteSuccessModal,
  }
}

function setFoundProjectFunc(state, action) {
  const foundProject = action.payload || {}
  return {
    ...state,
    foundProject,
  }
}

function setAddProjectSuccessModalVisibilityFunc(state, action) {
  const addProjectSuccessModalVisibility = action.payload || false
  return {
    ...state,
    addProjectSuccessModalVisibility,
  }
}

function setSelectedProjectFunc(state, action) {
  const selectedProject = action.payload || {}
  return {
    ...state,
    selectedProject,
  }
}

function setPayInvoiceModalFunc(state, action) {
  const payInvoiceModal = action.payload || false
  return {
    ...state,
    payInvoiceModal,
  }
}

function setBankSuccessFunc(state, action) {
  const bankSuccess = action.payload || false
  return {
    ...state,
    bankSuccess,
  }
}

function setCardSuccessFunc(state, action) {
  const cardSuccess = action.payload || false
  return {
    ...state,
    cardSuccess,
  }
}

const reducer = createReducer({}, function invoiceDetailsReducer(builder) {
  builder.addCase(
    actions.setInvoiceStatusModalVisibility,
    setInvoiceStatusModalVisibilityFunc,
  )
  builder.addCase(actions.setInvoicePaymentStatus, setInvoicePaymentStatusFunc)
  builder.addCase(
    actions.setInvoicePaymentMessage,
    setInvoicePaymentMessageFunc,
  )
  builder.addCase(actions.setInvoicePaymentMethod, setInvoicePaymentMethodFunc)
  builder.addCase(
    actions.setPaymentPlanModalVisibility,
    setPaymentPlanModalVisibilityFunc,
  )
  builder.addCase(
    actions.setAddToProjectModalVisibility,
    setAddToProjectModalVisibilityFunc,
  )
  builder.addCase(
    actions.setProjectSelectionModalVisibility,
    setProjectSelectionModalVisibilityFunc,
  )
  builder.addCase(
    actions.setPaymentProcessingModal,
    setPaymentProcessingModalFunc,
  )
  builder.addCase(actions.setInviteSuccessModal, setInviteSuccessModalFunc)
  builder.addCase(actions.setFoundProject, setFoundProjectFunc)
  builder.addCase(
    actions.setAddProjectSuccessModalVisibility,
    setAddProjectSuccessModalVisibilityFunc,
  )
  builder.addCase(actions.setSelectedProject, setSelectedProjectFunc)
  builder.addCase(actions.setPayInvoiceModal, setPayInvoiceModalFunc)
  builder.addCase(actions.setBankSuccess, setBankSuccessFunc)
  builder.addCase(actions.setCardSuccess, setCardSuccessFunc)
})

export function useInvoiceDetailsContext() {
  const context: any = useContext(InvoiceDetailsContext)
  const [state, dispatch] = context
  const {
    invoiceStatusModalVisibility = false,
    invoicePaymentStatus = '',
    invoicePaymentMessage = '',
    invoicePaymentMethod = {},
    paymentPlanModalVisibility = false,
    addToProjectModalVisibility = false,
    projectSelectionModalVisibility = false,
    paymentProcessingModal = false,
    inviteSuccessModal = false,
    foundProject = {},
    addProjectSuccessModalVisibility = false,
    selectedProject = {},
    payInvoiceModal = false,
    bankSuccess = false,
    cardSuccess = false,
  } = state
  const {
    setInvoiceStatusModalVisibility,
    setInvoicePaymentStatus,
    setInvoicePaymentMessage,
    setInvoicePaymentMethod,
    setPaymentPlanModalVisibility,
    setAddToProjectModalVisibility,
    setProjectSelectionModalVisibility,
    setPaymentProcessingModal,
    setInviteSuccessModal,
    setFoundProject,
    setAddProjectSuccessModalVisibility,
    setSelectedProject,
    setPayInvoiceModal,
    setBankSuccess,
    setCardSuccess,
  } = useMemo(
    () => ({
      setInvoiceStatusModalVisibility(value = false) {
        dispatch(actions.setInvoiceStatusModalVisibility(value))
      },
      setInvoicePaymentStatus(value = '') {
        dispatch(actions.setInvoicePaymentStatus(value))
      },
      setInvoicePaymentMessage(value = false) {
        dispatch(actions.setInvoicePaymentMessage(value))
      },
      setInvoicePaymentMethod(value = {}) {
        dispatch(actions.setInvoicePaymentMethod(value))
      },
      setPaymentPlanModalVisibility(value = false) {
        dispatch(actions.setPaymentPlanModalVisibility(value))
      },
      setAddToProjectModalVisibility(value = false) {
        dispatch(actions.setAddToProjectModalVisibility(value))
      },
      setProjectSelectionModalVisibility(value = false) {
        dispatch(actions.setProjectSelectionModalVisibility(value))
      },
      setPaymentProcessingModal(value = false) {
        dispatch(actions.setPaymentProcessingModal(value))
      },
      setInviteSuccessModal(value = false) {
        dispatch(actions.setInviteSuccessModal(value))
      },
      setFoundProject(value = {}) {
        dispatch(actions.setFoundProject(value))
      },
      setAddProjectSuccessModalVisibility(value = false) {
        dispatch(actions.setAddProjectSuccessModalVisibility(value))
      },
      setSelectedProject(value = {}) {
        dispatch(actions.setSelectedProject(value))
      },
      setPayInvoiceModal(value = false) {
        dispatch(actions.setPayInvoiceModal(value))
      },
      setBankSuccess(value = false) {
        dispatch(actions.setBankSuccess(value))
      },
      setCardSuccess(value = false) {
        dispatch(actions.setCardSuccess(value))
      },
    }),
    [dispatch],
  )
  return {
    invoiceStatusModalVisibility,
    invoicePaymentStatus,
    invoicePaymentMessage,
    invoicePaymentMethod,
    paymentPlanModalVisibility,
    addToProjectModalVisibility,
    projectSelectionModalVisibility,
    paymentProcessingModal,
    inviteSuccessModal,
    foundProject,
    addProjectSuccessModalVisibility,
    selectedProject,
    payInvoiceModal,
    bankSuccess,
    cardSuccess,
    setInvoiceStatusModalVisibility,
    setInvoicePaymentStatus,
    setInvoicePaymentMessage,
    setInvoicePaymentMethod,
    setPaymentPlanModalVisibility,
    setAddToProjectModalVisibility,
    setProjectSelectionModalVisibility,
    setPaymentProcessingModal,
    setInviteSuccessModal,
    setFoundProject,
    setAddProjectSuccessModalVisibility,
    setSelectedProject,
    setPayInvoiceModal,
    setBankSuccess,
    setCardSuccess,
  }
}

export function InvoiceDetailsProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, {})
  const value: any = useMemo(() => [state, dispatch], [state, dispatch])
  return (
    <InvoiceDetailsContext.Provider value={value}>
      {children}
    </InvoiceDetailsContext.Provider>
  )
}
